import { describeKnowledgeApiError } from 'utils/knowledgeErrors';
import { LoadingOutlined } from '@ant-design/icons';
import { useGetSegmentsValue } from '@knowledge/common/share';
import ConfigItem from '@knowledge/knowledges/singleClass/ConfigItem';
import {
  ClassActiveKnowledgeIdContext,
  CurrentKnowledgeValueProvider,
} from '@knowledge/knowledges/singleClass/context';
import { getSpuClassKnowledgeDetail } from '@knowledge/knowledges/singleClassNew/api';
import { useTranslate } from '@tolgee/react';
import { useMount } from 'ahooks';
import { Drawer, DrawerProps, message } from 'antd';
import useCrud from 'hooks/useCrud';
import { sortBy } from 'lodash-es';
import { useRef } from 'react';
import isEqual from 'react-fast-compare';
import { CiteKnowledge, SaveKnowledge } from 'services/api/supplementKnowledge';

function ItemKnowledgeDrawer({
  kvId,
  pointId,
  knowledgeId,
  knowledge,
  onClose,
  onKnowledgesChange,
  ...otherProps
}: DrawerProps & {
  kvId: number;
  knowledgeId: number;
  pointId: string;
  knowledge: CiteKnowledge;
  onKnowledgesChange: (knowledges: SaveKnowledge[]) => void;
}) {
  const { key, source } = knowledge;

  const { t } = useTranslate();

  const initialItemRef = useRef<Knowledge.SingleClass.Knowledge>();

  const { loading, item, setItem, fetch } = useCrud({
    read: () =>
      getSpuClassKnowledgeDetail({ knowledgeId: kvId }).then((res) => {
        const { values, ...rest } = res;
        const initialItem = {
          ...rest,
          values: sortBy(values, ({ id, isDefaultValue }) =>
            id === `${knowledgeId}` ? 0 : isDefaultValue ? 1 : 2,
          ),
        } as any as Knowledge.SingleClass.Knowledge;
        initialItemRef.current = initialItem;
        return initialItem;
      }),
  });

  const { getSegmentsValue } = useGetSegmentsValue(item?.valueAsDirectAnswer);

  useMount(() => {
    return fetch().catch((knowledgeError) => {
      message.error(
        describeKnowledgeApiError(knowledgeError, undefined, t('phrase.获取数据失败，请稍后再试')),
      );
    });
  });

  return (
    <Drawer
      title={item ? `${t('修改引用知识')} -【${item.knowledgeName}】` : t('修改引用知识')}
      width={1200}
      onClose={(e) => {
        onClose?.(e);
        if (!initialItemRef.current || !item) {
          return;
        }
        const deletedValues = initialItemRef.current.values.filter(({ id }) => {
          return item.values.every((_) => _.id !== id);
        });
        const newValues = item.values.filter(({ id }) => id && id.startsWith('fake'));
        const updatedValues = item.values.filter((value) => {
          const target = initialItemRef.current?.values.find(({ id }) => id === value.id);
          if (!target) {
            return false;
          }
          const segmentsEqual = isEqual(value.segments, target.segments);
          const spusEqual = isEqual(value.spus, target.spus);
          return !segmentsEqual || !spusEqual || !(value.isDefaultValue === target.isDefaultValue);
        });
        const value2Knowledge = (
          knowledgeValue: Knowledge.SPU.KnowledgeValue,
          operate: SaveKnowledge['operate'],
        ): SaveKnowledge => {
          const { id, spus, isDefaultValue } = knowledgeValue;
          return {
            knowledgeId: (operate === 'CREATE' ? undefined : Number(id)) as any,
            kvId,
            pointId,
            key,
            source,
            value: getSegmentsValue(knowledgeValue).map(({ type, content }) => {
              if (type === 'TEXT') {
                return {
                  msgType: type,
                  message: content,
                };
              }
              return {
                msgType: type,
                url: content,
              };
            }),
            bindItem:
              spus?.map(({ itemInternalId, ...others }) => ({
                ...others,
                itemInternalId: Number(itemInternalId),
              })) || [],
            operate,
            isDefault: isDefaultValue,
          };
        };
        const knowledges = [
          ...updatedValues.map((value) => value2Knowledge(value, 'UPDATE')),
          ...newValues.map((value) => value2Knowledge(value, 'CREATE')),
          ...deletedValues.map((value) => value2Knowledge(value, 'DELETE')),
        ];
        onKnowledgesChange(knowledges);
      }}
      {...otherProps}
    >
      {loading ? (
        <LoadingOutlined />
      ) : item ? (
        <div className="h-full">
          <ClassActiveKnowledgeIdContext.Provider value={{ activeId: item.id }}>
            <CurrentKnowledgeValueProvider>
              <ConfigItem
                citeMode
                activeKnowledgeId={knowledgeId}
                knowledge={item}
                onKnowledgeUpdate={setItem}
              />
            </CurrentKnowledgeValueProvider>
          </ClassActiveKnowledgeIdContext.Provider>
        </div>
      ) : null}
    </Drawer>
  );
}

export default ItemKnowledgeDrawer;
